import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Heart, Trash2, Globe, User } from "lucide-react";
import { Button } from "@/components/ui/button";

const FavoritesPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [universes, setUniverses] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [favRes, uniRes, charRes] = await Promise.all([
          axios.get(`${API}/favorites`),
          axios.get(`${API}/universes`),
          axios.get(`${API}/characters`),
        ]);
        setFavorites(favRes.data);
        setUniverses(uniRes.data);
        setCharacters(charRes.data);
      } catch (error) {
        console.error("Failed to fetch favorites:", error);
        toast.error("Failed to load favorites");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const removeFavorite = async (favoriteId) => {
    try {
      await axios.delete(`${API}/favorites/${favoriteId}`);
      setFavorites(favorites.filter(f => f.id !== favoriteId));
      toast.success("Removed from favorites");
    } catch (error) {
      toast.error("Failed to remove favorite");
    }
  };

  const universeFavorites = favorites.filter(f => f.target_type === "universe");
  const characterFavorites = favorites.filter(f => f.target_type === "character");

  const getUniverseData = (universeId) => universes.find(u => u.id === universeId);
  const getCharacterData = (characterId) => characters.find(c => c.id === characterId);

  return (
    <div className="min-h-screen flex flex-col bg-[#030712]">
      <Navbar />
      
      <main className="flex-1 pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-8">
            <Heart className="w-8 h-8 text-yellow-500" />
            <h1 className="text-3xl sm:text-4xl font-black text-white font-['Outfit']">{t("favorites.title")}</h1>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="spinner"></div>
            </div>
          ) : favorites.length === 0 ? (
            <div className="text-center py-20">
              <Heart className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h2 className="text-xl text-white mb-2">{t("favorites.empty")}</h2>
              <p className="text-slate-400 mb-6">Start exploring and save your favorite universes and characters!</p>
              <Link to="/" className="btn-primary px-6 py-3 inline-block">
                Explore Universes
              </Link>
            </div>
          ) : (
            <div className="space-y-12">
              {/* Favorite Universes */}
              {universeFavorites.length > 0 && (
                <section>
                  <h2 className="flex items-center gap-2 text-xl font-bold text-white mb-6 font-['Outfit']">
                    <Globe className="w-5 h-5 text-amber-500" />
                    Favorite Universes
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {universeFavorites.map((fav) => {
                      const universe = getUniverseData(fav.target_id);
                      if (!universe) return null;
                      return (
                        <div key={fav.id} className="card-surface overflow-hidden group">
                          <Link to={`/universe/${universe.slug}`}>
                            <div className="relative aspect-video">
                              <img 
                                src={universe.cover_image || "https://static.prod-images.emergentagent.com/jobs/f60715c2-3b6b-4c99-a6c8-5c96b98e6cf9/images/53108f7d8ec9c28606e03e3c421e51fe4b80f13705cd9eeafc38c88182bd88ad.png"}
                                alt={universe.name}
                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-[#030712] to-transparent" />
                            </div>
                          </Link>
                          <div className="p-4 flex items-center justify-between">
                            <Link to={`/universe/${universe.slug}`} className="flex-1">
                              <h3 className="text-lg font-bold text-white group-hover:text-yellow-500 transition-colors font-['Outfit']">
                                {universe.name}
                              </h3>
                              <span className="text-xs text-amber-500 uppercase">{universe.category}</span>
                            </Link>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeFavorite(fav.id)}
                              className="text-slate-400 hover:text-red-500"
                              data-testid={`remove-fav-${fav.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* Favorite Characters */}
              {characterFavorites.length > 0 && (
                <section>
                  <h2 className="flex items-center gap-2 text-xl font-bold text-white mb-6 font-['Outfit']">
                    <User className="w-5 h-5 text-amber-500" />
                    Favorite Characters
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {characterFavorites.map((fav) => {
                      const character = getCharacterData(fav.target_id);
                      if (!character) return null;
                      return (
                        <div key={fav.id} className="card-surface overflow-hidden group">
                          <Link to={`/character/${character.id}`}>
                            <div className="relative aspect-[3/4]">
                              <img 
                                src={character.image || "https://static.prod-images.emergentagent.com/jobs/f60715c2-3b6b-4c99-a6c8-5c96b98e6cf9/images/c87d923edbc4557b647ae3b5b7af92b6c1f0325cd0bb5d5d8bdfa7f98e780e7d.png"}
                                alt={character.name}
                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-[#030712] via-transparent to-transparent" />
                            </div>
                          </Link>
                          <div className="p-3 flex items-center justify-between">
                            <Link to={`/character/${character.id}`} className="flex-1 min-w-0">
                              <h3 className="text-sm font-bold text-white group-hover:text-yellow-500 transition-colors truncate font-['Outfit']">
                                {character.name}
                              </h3>
                            </Link>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeFavorite(fav.id)}
                              className="text-slate-400 hover:text-red-500 p-1"
                              data-testid={`remove-fav-${fav.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FavoritesPage;
